import React, { useRef, useState } from "react";
import axios from "axios";
import { FileText, FileSpreadsheet, Image as ImageIcon, File as FileIcon, Upload, X, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*";

function DocIcon({ ct = "", name = "" }) {
  const n = (name || "").toLowerCase();
  if (ct.startsWith("image/")) return <ImageIcon className="w-4 h-4 text-sky-500 flex-shrink-0" />;
  if (ct === "application/pdf" || n.endsWith(".pdf")) return <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (ct.includes("sheet") || ct.includes("excel") || n.match(/\.(xls|xlsx|csv)$/)) return <FileSpreadsheet className="w-4 h-4 text-green-600 flex-shrink-0" />;
  if (ct.includes("word") || n.match(/\.(doc|docx)$/)) return <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />;
  return <FileIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />;
}

// Admin-only box for sensitive offers/documents (images, PDF, Excel, Word).
// Uploads immediately (returns metadata) and reports the doc list up via onChange.
export default function AdminDocsBox({ docs = [], onChange, authHeader, readOnly = false, title = "Dokumenter (kun admin)" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const tokenStr = () => {
    try { return (authHeader().headers.Authorization || "").replace("Bearer ", ""); } catch { return ""; }
  };
  const viewUrl = (d) => `${API}/doserne/attachments/${d.id}/download?auth=${encodeURIComponent(tokenStr())}`;

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    const added = [];
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f, f.name || "fil");
        const h = authHeader();
        const { data } = await axios.post(`${API}/doserne/attachments/upload`, fd, {
          ...h, headers: { ...h.headers, "Content-Type": "multipart/form-data" },
        });
        added.push(data);
      } catch {
        toast.error(`Kunne ikke laste opp ${f.name || "fil"}`);
      }
    }
    setBusy(false);
    if (added.length) {
      onChange?.([...(docs || []), ...added]);
      toast.success(`${added.length} fil(er) lastet opp`);
    }
  };

  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); uploadFiles(files); }
  };

  const remove = (id) => onChange?.((docs || []).filter((d) => d.id !== id));

  return (
    <div
      className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-3"
      onPaste={readOnly ? undefined : onPaste}
      tabIndex={readOnly ? undefined : 0}
      data-testid="admin-docs-box"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-amber-700 mb-2">
        <Lock className="w-3.5 h-3.5" /> {title}
      </div>

      {(docs || []).length > 0 && (
        <div className="space-y-1.5 mb-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 bg-white rounded-lg border border-amber-200 px-2 py-1.5 text-sm">
              <DocIcon ct={d.content_type} name={d.filename} />
              <a href={viewUrl(d)} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-slate-800 hover:underline" title={d.filename} data-testid={`admin-doc-${d.id}`}>
                {d.filename}
              </a>
              {!readOnly && (
                <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-500" title="Fjern" data-testid={`admin-doc-remove-${d.id}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly ? (
        <>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-50"
            data-testid="admin-docs-upload"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Last opp / lim inn (Ctrl+V)
          </button>
        </>
      ) : (
        (docs || []).length === 0 && <div className="text-xs text-slate-400">Ingen dokumenter</div>
      )}
    </div>
  );
}
